/**
 * §onboarding-activation (decision #3) — first-login "set your password".
 *
 * Reached right after a magic-link login when the server signals
 * `needs_password_set` (the Owner is on a random bootstrap password they never
 * saw). Calling `setInitialPassword` hits the gated `/api/admin/me/set-initial-
 * password` endpoint (no current password required, but server-gated to the
 * bootstrap state) and lands a fresh session, so day-2 re-entry works with a
 * password the Owner actually chose.
 *
 * The Owner is already authenticated here (the magic login stored a session),
 * so "skip for now" simply proceeds to the dashboard on that session.
 */
import { useState } from "react";
import { useNavigate } from "react-router";

import { ApiError, setInitialPassword } from "@/lib/api";

export function SetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= 12 && confirm === password && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await setInitialPassword(password);
      navigate("/inbox", { replace: true });
    } catch (err) {
      // The server enforces strength (min 12 chars, ≥3 character classes) and
      // returns the exact reason; surface it verbatim.
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't set your password. Please try again.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg, #0b0d12)",
        padding: 24,
      }}
    >
      <div
        className="onb-glass"
        style={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 16,
          padding: "32px 30px",
          color: "var(--fg, #e8eaf0)",
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") void submit();
        }}
      >
        <h1 style={{ fontSize: 22, margin: "0 0 6px" }}>Set your password</h1>
        <p style={{ fontSize: 14, opacity: 0.7, margin: "0 0 22px", lineHeight: 1.5 }}>
          Your agent is live. Choose a password so you can sign back in anytime.
        </p>

        <label style={{ display: "block", fontSize: 13, opacity: 0.8, marginBottom: 6 }}>
          New password
        </label>
        <input
          type="password"
          aria-label="New password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 12 characters"
          style={inputStyle}
        />
        {tooShort && (
          <div style={hintStyle}>Use at least 12 characters.</div>
        )}

        <label
          style={{ display: "block", fontSize: 13, opacity: 0.8, margin: "16px 0 6px" }}
        >
          Confirm password
        </label>
        <input
          type="password"
          aria-label="Confirm password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Re-enter your password"
          style={inputStyle}
        />
        {mismatch && <div style={hintStyle}>Passwords don't match.</div>}

        {error && (
          <div role="alert" style={{ ...hintStyle, color: "var(--danger, #ff6b6b)" }}>
            {error}
          </div>
        )}

        <button
          className="onb-btn onb-btn-primary onb-btn-lg onb-btn-block"
          disabled={!canSubmit}
          onClick={() => void submit()}
          style={{ width: "100%", marginTop: 22, opacity: canSubmit ? 1 : 0.6 }}
        >
          {submitting ? "Saving…" : "Set password & continue"}
        </button>
        <button
          className="onb-btn onb-btn-ghost"
          onClick={() => navigate("/inbox", { replace: true })}
          style={{ width: "100%", marginTop: 10, background: "transparent" }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 44,
  borderRadius: 10,
  border: "1px solid var(--hairline, rgba(255,255,255,0.12))",
  background: "var(--field, rgba(255,255,255,0.04))",
  color: "inherit",
  padding: "0 14px",
  fontSize: 15,
  outline: "none",
  boxSizing: "border-box",
};

const hintStyle: React.CSSProperties = {
  fontSize: 12.5,
  opacity: 0.85,
  marginTop: 8,
};

export default SetPasswordPage;
